import { push } from 'connected-react-router';
import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { HttpMethods, SERVICES } from '../../services/api';
import { basicApiAsyncThunkCreator, TokenTypes } from '../../services/apiAsyncThunkCreator';
import { ThunkAPI } from '../rootReducer';
import { IUserLoginResponse } from '../auth/login.thunks';
import { showSuccessMessage, showWarningMessage } from '../notifications';
import { SignUpTier } from '../../components/auth/signUp/types';
import { IUtmParams } from '../../components/auth/signUp/SignUpByEmail';

export interface ISocialSignUpRequest {
  token: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

export interface ICredentialsSignUpRequest extends ISocialSignUpRequest {
  email: string;
  password: string;
}

export interface ISignUpByEmailRequest {
  email: string;
  recaptchaToken: string;
  useFallback: boolean;
  tier: SignUpTier;
  utmParams?: IUtmParams;
}

export interface ISignUpByEmailAccessTypeResponse {
  redirectToLogin: boolean;
  redirectToLoginWithLiteWarning: boolean;
  redirectToEmailLinkSent: boolean;
  redirectToSubscription: boolean;
  accessType?: string;
}

export interface ISignUpByEmailResponse {
  email: string;
}

export const userSignUp: AsyncThunk<IUserLoginResponse, ICredentialsSignUpRequest, ThunkAPI> = createAsyncThunk(
  'auth/signUp',
  async (signUpRequest: ICredentialsSignUpRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IUserLoginResponse, ICredentialsSignUpRequest>({
        endpoint: 'signUp',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(signUpRequest, thunk) as IUserLoginResponse;

      return response;
    } catch (error) {
      return thunk.rejectWithValue(error);
    }
  }
);

export const userSocialSignUp: AsyncThunk<IUserLoginResponse, ISocialSignUpRequest, ThunkAPI> = createAsyncThunk(
  'auth/socialSignUp',
  async (signUpRequest: ISocialSignUpRequest, thunk) => {
    const { token, ...data } = signUpRequest;

    try {
      const response = await basicApiAsyncThunkCreator<IUserLoginResponse, Omit<ISocialSignUpRequest, 'token'>>({
        endpoint: 'social-sign-up',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json',
        cognitoToken: `${TokenTypes.Cognito} ${token}`
      })(data, thunk) as IUserLoginResponse;

      return response;
    } catch (error) {
      return thunk.rejectWithValue(error);
    }
  }
);

export interface ICheckEmailResponse {
  used: boolean;
}

export interface ICheckEmailRequest {
  email: string;
  token: string;
}

export const checkEmail: AsyncThunk<ICheckEmailResponse, ICheckEmailRequest, ThunkAPI> = createAsyncThunk(
  'auth/signUp/checkEmail',
  async (data: ICheckEmailRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<ICheckEmailResponse, ICheckEmailRequest>({
        endpoint: 'check-email',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk) as ICheckEmailResponse;

      return response;
    } catch (error) {
      return thunk.rejectWithValue(error);
    }
  }
);

export const signUpByEmail: AsyncThunk<ISignUpByEmailResponse, ISignUpByEmailRequest, ThunkAPI> = createAsyncThunk(
  'auth/signUp/by-email',
  async (data: ISignUpByEmailRequest, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<ISignUpByEmailAccessTypeResponse, ISignUpByEmailRequest>({
        endpoint: 'signUp/by-email',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);

      const emailResponse = {
        email: data.email
      };

      if ('redirectToSubscription' in response) {
        thunk.dispatch(push('/signUp/pro/subscription'));
        return emailResponse;
      }

      if ('redirectToLoginWithLiteWarning' in response) {
        thunk.dispatch(showWarningMessage('You can upgrade your account to the Professional tier'));
        return emailResponse;
      }

      if ('redirectToEmailLinkSent' in response) {
        thunk.dispatch(push('/signUp/resend'));
        return emailResponse;
      }

      thunk.dispatch(push('/login'));
      return emailResponse;
    } catch (error) {
      return thunk.rejectWithValue(error);
    }
  }
);

export const resendSignUpEmail: AsyncThunk<ISignUpByEmailResponse, ISignUpByEmailRequest, ThunkAPI> = createAsyncThunk(
  'auth/signUp/resend',
  async (data: ISignUpByEmailRequest, thunk) => {
    try {
      await basicApiAsyncThunkCreator<void, ISignUpByEmailRequest>({
        endpoint: '/signUp/resend',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: false,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);

      thunk.dispatch(showSuccessMessage('Your email has been successfully sent'));

      return {
        email: data.email
      };
    } catch (error) {
      return thunk.rejectWithValue(error);
    }
  }
);
